/**
 * The search-service client.
 *
 * Every call in this file is one a prospect could make from their own backend
 * with the same key — that is deliberate, and it is what the demo is for. There
 * is no demo backend translating anything: the network tab shows the real API.
 *
 * The key travels as `Authorization: Bearer`, never as a cookie, so nothing is
 * attached automatically and the service can keep CORS credentials off.
 */

export const API_BASE = (import.meta.env.VITE_SEARCH_API_URL || '').replace(/\/$/, '');

/** Identifies demo traffic in the service's logs. Unverified and never used for
 *  authorisation — it exists so demo usage is separable from a tenant's own. */
const CLIENT = 'demo-app';

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body?: unknown) {
    super(message);
  }

  /** What to actually show a prospect. The service's own `detail` is written for
   *  integrators and is usually right; these cover the cases where it is not. */
  get friendly(): string {
    if (this.status === 401) return 'That key was not accepted. Check for a missing character, or ask us for a new one.';
    if (this.status === 402) return 'This demo has expired. Ask us to extend it and the same key will start working again.';
    if (this.status === 403) return 'This key is valid but is missing a permission this action needs.';
    if (this.status === 429) return 'The demo quota for this month has been used up.';
    if (this.status === 0) return `Could not reach the API at ${API_BASE || 'this origin'}. It may be down, or this page's origin may not be allowed to call it.`;
    return this.message;
  }
}

async function request<T>(path: string, key: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${key}`,
        'x-client': CLIENT,
        ...(init.headers ?? {}),
      },
    });
  } catch (error) {
    // fetch rejects for a network failure AND for a CORS rejection, with no way
    // to tell them apart from script — the browser withholds that deliberately.
    // Status 0 carries "we never got an answer", and ApiError.friendly names both
    // possibilities rather than guessing.
    throw new ApiError(0, error instanceof Error ? error.message : 'Network error');
  }

  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }

  if (!response.ok) {
    const detail = (body as { detail?: string })?.detail ?? `${response.status} ${response.statusText}`;
    throw new ApiError(response.status, detail, body);
  }
  return body as T;
}

/* ------------------------------------------------------------------ *
 * Types — a subset of search-service/src/types.ts, hand-copied.
 *
 * Not imported from that package: the demo is a static site that must build
 * without the service's toolchain, and a shared types package for four
 * interfaces would couple a sales asset to a deployment unit.
 * ------------------------------------------------------------------ */

export interface UsageResponse {
  tenant: {
    slug: string;
    status: string;
    /** The tenant's configured markets, first one primary. May be absent on an
     *  older search-service, which is why every consumer treats it as optional. */
    markets?: string[];
    currency?: string | null;
  };
  documents: { total: number; indexed: number; pending: number; failed: number };
  quotas?: Record<string, number>;
}

export interface MatchReason {
  kind: 'match' | 'neutral' | 'caution';
  label: string;
  tag?: string;
}

export interface CommuteEstimate {
  estimateMin: number;
  rangeMin: [number, number];
  withinBudget: boolean;
  confidence: 'low' | 'medium' | 'high' | 'unavailable';
  mode: 'transit' | 'car' | 'walk';
}

export interface SearchResultItem {
  doc_id: string;
  score: number;
  matchReasons: MatchReason[];
  commute?: CommuteEstimate;
  fields?: {
    title?: string | null;
    price?: number | null;
    currency?: string | null;
    bedrooms?: number | null;
    bathrooms?: number | null;
    area_name?: string | null;
    city?: string | null;
  };
  explain?: Record<string, number>;
}

export interface ParsedQuery {
  bedrooms?: { min?: number; max?: number };
  bathrooms?: { min?: number; max?: number };
  price?: { min?: number; max?: number; currency?: string };
  areas?: string[];
  cities?: string[];
  amenities?: string[];
  tags?: string[];
  commute?: { label: string; maxMinutes: number; mode: string };
  freeText?: string | null;
}

export interface SearchResponse {
  parsed: ParsedQuery;
  results: SearchResultItem[];
  total: number;
  suggestions?: Array<{ label: string; patch: Partial<ParsedQuery>; extraResults?: number }>;
  warnings?: string[];
  diagnostics?: Record<string, unknown>;
}

export interface BatchResponse {
  accepted: number;
  unchanged: number;
  rejected: number;
  results: Array<{ doc_id: string; status: string; reason?: string }>;
}

export interface DocumentState {
  doc_id: string;
  index_state: 'pending' | 'indexed' | 'failed';
  index_error?: string | null;
  has_embedding: boolean;
  location: {
    lat?: number | null;
    lon?: number | null;
    precision?: string | null;
    source?: string | null;
    market?: string | null;
    metro?: string | null;
  };
  geo_tags: string[];
}

/* ------------------------------------------------------------------ */

/** Doubles as key validation: it needs the `search` scope and 401s without it. */
export function validateKey(key: string): Promise<UsageResponse> {
  return request<UsageResponse>('/v1/usage', key);
}

export function search(key: string, query: string, signal?: AbortSignal): Promise<SearchResponse> {
  return request<SearchResponse>('/v1/search', key, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      query,
      limit: 20,
      options: {
        // Without this, search returns ids and evidence only and the caller
        // hydrates from its own database — right for production, useless for a
        // demo that has no database of its own.
        include_fields: true,
        explain: true,
      },
    }),
  });
}

export interface NewListing {
  doc_id: string;
  title: string;
  body?: string;
  price?: number;
  currency?: string;
  bedrooms?: number;
  bathrooms?: number;
  area_name?: string;
  city?: string;
  amenities?: string[];
  address: { street?: string; suburb?: string; city?: string; text?: string };
}

export function upsert(key: string, listing: NewListing): Promise<BatchResponse> {
  return request<BatchResponse>('/v1/documents:batch', key, {
    method: 'POST',
    body: JSON.stringify({ documents: [listing] }),
  });
}

export function getDocument(key: string, docId: string): Promise<DocumentState> {
  return request<DocumentState>(`/v1/documents/${encodeURIComponent(docId)}`, key);
}
